/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { QUERY_PERSIST_CACHE } from '@airasia-rn/ui';
import {
  SNAP_DOMAIN,
  SNAP_DESTINATIONS,
  SNAP_LFC,
  SNAP_RECENT_SEARCHES,
} from '../../configs/constants';
import type { SnapListingParams } from '../../types';
import type { SnapChangeFlightsRequest } from '../../types/flightListing';

const searchKeyRoots = {
  destinations: [QUERY_PERSIST_CACHE, SNAP_DOMAIN, SNAP_DESTINATIONS] as const,
  banner: [QUERY_PERSIST_CACHE, SNAP_DOMAIN, 'banner'] as const,
  lfc: [SNAP_DOMAIN, SNAP_LFC] as const,
  listing: [SNAP_DOMAIN, 'listing'] as const,
  locationFilter: [SNAP_DOMAIN, 'location-filter'] as const,
  smartFill: [SNAP_DOMAIN, 'smart-fill'] as const,
  recommendations: [SNAP_DOMAIN, 'hotel-recommendations'] as const,
  recentSearches: [
    QUERY_PERSIST_CACHE,
    SNAP_DOMAIN,
    SNAP_RECENT_SEARCHES,
  ] as const,
};

const getSnapListingFilterKeyPart = (value?: string | string[]) =>
  Array.isArray(value) ? [...value].sort().join(',') : value;

/**
 * Snap TanStack Query keys, grouped by screen domain (same idea as hotelsKeys).
 * - `search` — search form + list (destinations, LFC, listing, recommendations, …)
 * - `details` — detail screen hooks (extend when those queries land)
 */
export const snapKeys = {
  search: {
    destinations: {
      all: searchKeyRoots.destinations,
      station: (stationCode: string) =>
        [...searchKeyRoots.destinations, stationCode.toUpperCase()] as const,
    },
    banner: {
      all: searchKeyRoots.banner,
      env: (env: string) => [...searchKeyRoots.banner, env] as const,
    },
    lfc: {
      all: searchKeyRoots.lfc,
      route: (
        departStation: string,
        arrivalStation: string,
        currency: string,
      ) =>
        [
          ...searchKeyRoots.lfc,
          departStation.toUpperCase(),
          arrivalStation.toUpperCase(),
          currency.toUpperCase(),
        ] as const,
    },
    listing: {
      all: searchKeyRoots.listing,
      params: (params: SnapListingParams) =>
        [
          ...searchKeyRoots.listing,
          params.tripType,
          params.orig,
          params.dest,
          params.fromDateFlight,
          params.toDateFlight,
          params.fromDateHotel,
          params.toDateHotel,
          params.rooms,
          params.adults,
          params.children,
          params.infants,
          params.currency,
          params.country_code,
          params.language,
          params.snapId,
          params.hotelSize,
          params.departFlightId,
          params.returnFlightId,
          params.guest_rating ?? params.guestRating,
          getSnapListingFilterKeyPart(params.starRating ?? params.star_rating),
          getSnapListingFilterKeyPart(params.propertyTypes ?? params.category),
          params.toLocationId,
        ] as const,
    },
    locationFilter: {
      all: searchKeyRoots.locationFilter,
      params: (location: string, language: string, env: string) =>
        [
          ...searchKeyRoots.locationFilter,
          location.toUpperCase(),
          language.toLowerCase(),
          env,
        ] as const,
    },
    smartFill: {
      all: searchKeyRoots.smartFill,
      params: (location: string, language: string, env: string) =>
        [
          ...searchKeyRoots.smartFill,
          location.toUpperCase(),
          language.toLowerCase(),
          env,
        ] as const,
    },
    recommendations: {
      all: searchKeyRoots.recommendations,
      params: (params: SnapListingParams) =>
        [
          ...searchKeyRoots.recommendations,
          params.orig,
          params.dest,
          params.fromDateFlight,
          params.toDateFlight,
          params.currency,
          params.country_code,
          params.language,
          params.rooms,
          params.adults,
        ] as const,
    },
    recentSearches: {
      all: searchKeyRoots.recentSearches,
    },
  },
  details: {
    hotelRooms: [SNAP_DOMAIN, 'hotel-rooms'] as const,
    tripHandoff: () =>
      [QUERY_PERSIST_CACHE, SNAP_DOMAIN, 'tripDetails', 'handoff'] as const,
    ancillarySummary: (contextId: string, snapId: string) =>
      ['snapAncillarySummary', contextId, snapId] as const,
  },
  changeFlights: {
    request: (request?: SnapChangeFlightsRequest) =>
      [SNAP_DOMAIN, 'change-flights', request] as const,
  },
} as const;

/** @deprecated Prefer `snapKeys.search.destinations` */
export const destinationsKeys = snapKeys.search.destinations;
/** @deprecated Prefer `snapKeys.search.banner` */
export const snapBannerKeys = snapKeys.search.banner;
/** @deprecated Prefer `snapKeys.search.lfc` */
export const snapLfcKeys = snapKeys.search.lfc;
/** @deprecated Prefer `snapKeys.search.listing` */
export const snapListingKeys = snapKeys.search.listing;
/** @deprecated Prefer `snapKeys.search.recommendations` */
export const snapRecommendationKeys = snapKeys.search.recommendations;
/** @deprecated Prefer `snapKeys.search.recentSearches` */
export const snapRecentSearchesKeys = snapKeys.search.recentSearches;
